import type { CSSProperties, ReactNode } from 'react'

import { useCopyToClipboard } from '../../useCopyToClipboard.ts'

// The exact body Curri POSTed, so a partner can diff it against what their
// handler received — presented as the design's dark console panel, the same
// surface as the landing's code console.
//
// Highlighting is a ~20-line tokenizer rather than a dependency. There is no
// syntax highlighter anywhere in the monorepo, `@curri/components` exports no
// CodeBlock (the one in its Storybook tree is not on the public surface, and the
// `sugar-high` it uses is a devDependency that will not resolve from here), and
// this bundle is otherwise React + components + Scalar + phosphor.
//
// Note this shows only the REQUEST body. The partner's own response is
// deliberately never returned by the API: the stored column carries their
// response headers, which can hold their auth tokens.

// A dark surface inside a light-theme app, so no semantic token resolves to its
// colours; literal, like the CONSOLE palette in ../landing/CodeConsole.tsx.
// Exported for the consoles composed on top of this one (bookings'
// RequestPreview styles its subheader/footer rows with the same palette).
export const PANEL = {
  surface: '#1C1917',
  line: 'rgba(255, 255, 255, 0.07)',
  buttonBorder: 'rgba(255, 255, 255, 0.12)',
  label: '#78716C',
  dim: '#57534E',
  subdued: '#A8A29E',
  key: '#E7E5E4',
  string: '#4EE7A6',
  literal: '#7DD3FC',
} as const

const TOKEN =
  /("(?:\\.|[^"\\])*"\s*:)|("(?:\\.|[^"\\])*")|(\b(?:true|false|null)\b)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g

const KEY: CSSProperties = { color: PANEL.key }
const STRING: CSSProperties = { color: PANEL.string }
const LITERAL: CSSProperties = { color: PANEL.literal }
const NUMBER: CSSProperties = { color: PANEL.literal }

const highlight = (json: string): ReactNode[] => {
  const nodes: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  TOKEN.lastIndex = 0
  while ((match = TOKEN.exec(json)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(
        <span key={lastIndex}>{json.slice(lastIndex, match.index)}</span>
      )
    }

    const [text, key, string, literal] = match
    const style = key ? KEY : string ? STRING : literal ? LITERAL : NUMBER
    nodes.push(
      <span key={match.index} style={style}>
        {text}
      </span>
    )
    lastIndex = match.index + text.length
  }

  if (lastIndex < json.length) {
    nodes.push(<span key={lastIndex}>{json.slice(lastIndex)}</span>)
  }
  return nodes
}

const stringify = (payload: unknown): string => {
  // `payload` is typed unknown for a reason: it is whatever JSON was stored, and
  // that includes JSON null and bare primitives, not just objects.
  try {
    return JSON.stringify(payload, null, 2) ?? 'null'
  } catch {
    return 'Could not render this payload.'
  }
}

const CopyPayloadButton = ({ text }: { text: string }) => {
  const { copied, copy, isSupported } = useCopyToClipboard()

  // `navigator.clipboard` is absent on an insecure origin; hiding the control is
  // better than offering one that silently does nothing.
  if (!isSupported) return null

  return (
    <button
      className="webhook-copy"
      onClick={event => {
        // The row behind this panel toggles on click; copying must not fold it.
        event.stopPropagation()
        copy(text)
      }}
      style={{
        height: 26,
        padding: '0 10px',
        borderRadius: 7,
        border: `1px solid ${PANEL.buttonBorder}`,
        background: 'transparent',
        color: copied ? PANEL.string : PANEL.subdued,
        fontFamily: 'var(--curri-font-mono)',
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
      }}
      type="button"
    >
      {copied ? 'copied' : 'copy'}
    </button>
  )
}

export const PayloadPreview = ({
  footer,
  maxHeight = 250,
  meta,
  payload,
  subheader,
  title = 'Request body',
}: {
  payload: unknown
  /** Uppercase console heading; the webhooks default names the stored body. */
  title?: string
  /** Muted mono annotation beside the title, e.g. "reconstructed". */
  meta?: string
  /** A row between the heading and the body — the bookings console puts the
   *  method/path line here. Rendered on the dark surface, so callers style
   *  with the exported PANEL palette. */
  subheader?: ReactNode
  /** A row under the body — status line, latency. Same surface note. */
  footer?: ReactNode
  maxHeight?: number
}) => {
  const json = stringify(payload)

  return (
    <div
      style={{
        background: PANEL.surface,
        border: `1px solid ${PANEL.line}`,
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: 'var(--shadow-elevation-raised)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px 10px 16px',
          borderBottom: `1px solid ${PANEL.line}`,
        }}
      >
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: PANEL.label,
            // The heading is two words; letting it wrap while the byte count
            // and copy button hold their width made the whole bar ragged.
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </span>
        {meta !== undefined && (
          <span
            style={{
              flex: '1 1 auto',
              minWidth: 0,
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 11,
              color: PANEL.dim,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {meta}
          </span>
        )}
        <span style={{ flex: meta === undefined ? 1 : 0 }} />
        <span
          style={{
            fontFamily: 'var(--curri-font-mono)',
            fontSize: 11,
            color: PANEL.dim,
          }}
        >
          {new TextEncoder().encode(json).length.toLocaleString()} B
        </span>
        <CopyPayloadButton text={json} />
      </div>
      {subheader !== undefined && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            padding: '10px 16px',
            borderBottom: `1px solid ${PANEL.line}`,
            fontFamily: 'var(--curri-font-mono)',
            fontSize: 11.5,
          }}
        >
          {subheader}
        </div>
      )}
      <pre
        style={{
          margin: 0,
          padding: '14px 18px',
          maxHeight,
          overflow: 'auto',
          fontFamily: 'var(--curri-font-mono)',
          fontSize: 11.5,
          lineHeight: 1.75,
          color: PANEL.subdued,
        }}
      >
        {highlight(json)}
      </pre>
      {footer !== undefined && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            flexWrap: 'wrap',
            padding: '10px 16px',
            borderTop: `1px solid ${PANEL.line}`,
            fontFamily: 'var(--curri-font-mono)',
            fontSize: 11.5,
          }}
        >
          {footer}
        </div>
      )}
    </div>
  )
}
